import { MoreOutlined, StarFilled, StarOutlined } from "@ant-design/icons"
import {
  Button,
  Card,
  Checkbox,
  Dropdown,
  Flex,
  Image,
  Skeleton,
  Tag,
  Typography
} from "antd"
import type { MenuItemType } from "antd/es/menu/interface"
import React, { memo, useState } from "react"
import { useTranslation } from "react-i18next"

import { getFileIcon, isImage } from "../../Project/ProjectViewPage/helpers/file.tsx"
import { type FileItem } from "../types/file-item.types.ts"

const { Text } = Typography

export const FILE_DRAG_MIME = "application/x-ezbuild-file-id"

interface FileCardProps {
  file: FileItem
  onPreview: (file: FileItem) => void
  actions: MenuItemType[]
  selected?: boolean
  onToggleSelect?: (file: FileItem) => void
  draggable?: boolean
  favorited?: boolean
  onToggleFavorite?: (file: FileItem) => void
}

export const FileCard = memo(
  ({
    file,
    onPreview,
    actions,
    selected = false,
    onToggleSelect,
    draggable = false,
    favorited = false,
    onToggleFavorite
  }: FileCardProps) => {
    const { t } = useTranslation()
    const [menuOpen, setMenuOpen] = useState(false)
    const isImg = isImage(file.mimeType)
    // Mounting the overlays only on hover shifted the card height by a few px.
    const overlayVisibleClass = `opacity-0 group-hover:opacity-100 transition-opacity ${
      selected || menuOpen ? "!opacity-100" : ""
    }`

    return (
      <Card
        hoverable
        draggable={draggable}
        onDragStart={(e) => {
          e.dataTransfer.setData(FILE_DRAG_MIME, file.key)
          e.dataTransfer.effectAllowed = "move"
        }}
        className={`relative size-full flex flex-col group ${selected ? "ring-2 ring-primary" : ""}`}
        classNames={{
          body: "p-2 flex-1",
          cover: "border rounded-t-card overflow-hidden border-border"
        }}
        onClick={() => onPreview(file)}
        cover={
          <div className="relative">
            {onToggleSelect && (
              <div
                className={`absolute top-1 left-1 z-10 ${overlayVisibleClass}`}
                onClick={(e) => {
                  e.stopPropagation()
                  onToggleSelect(file)
                }}
              >
                <Checkbox checked={selected} />
              </div>
            )}
            {actions.length > 0 && (
              <div
                className={`absolute top-1 right-1 z-10 ${overlayVisibleClass}`}
                onClick={(e) => e.stopPropagation()}
              >
                <Dropdown
                  trigger={["click"]}
                  open={menuOpen}
                  onOpenChange={setMenuOpen}
                  menu={{ items: actions }}
                >
                  <Button size="small" icon={<MoreOutlined />} />
                </Dropdown>
              </div>
            )}
            {onToggleFavorite && (
              <div
                className={`absolute bottom-1 right-1 z-10 ${
                  favorited ? "" : overlayVisibleClass
                }`}
                onClick={(e) => {
                  e.stopPropagation()
                  onToggleFavorite(file)
                }}
              >
                <Button
                  size="small"
                  shape="circle"
                  icon={
                    favorited ? (
                      <StarFilled className="text-warning" />
                    ) : (
                      <StarOutlined />
                    )
                  }
                />
              </div>
            )}
            {isImg && file.fileUrl ? (
              <div className="aspect-square overflow-hidden bg-primary/10">
                <Image
                  loading="lazy"
                  rootClassName="size-full"
                  alt={file.name}
                  src={file.fileUrl}
                  className="object-cover size-full"
                  preview={false}
                  placeholder={<Skeleton.Node className="size-full" />}
                />
              </div>
            ) : (
              <div className="aspect-square bg-primary/10 flex items-center justify-center">
                <div className="[&_svg]:text-5xl">{getFileIcon(file.mimeType)}</div>
              </div>
            )}
          </div>
        }
      >
        <Flex vertical gap={2}>
          <Text ellipsis={{ tooltip: file.name }} className="text-xs" strong>
            {file.name}
          </Text>
          <Flex gap={4} align="center">
            {file.fileTypeLabel && (
              <Text type="secondary" className="text-xs">
                {file.fileTypeLabel}
              </Text>
            )}
            {file.version !== undefined && (
              <Text type="secondary" className="text-xs">
                {t("fileViewer.detailsPanel.versionShort", { version: file.version })}
              </Text>
            )}
            <Text type="secondary" className="text-xs">
              {file.created?.slice(0, 10)}
            </Text>
          </Flex>
          {(file.documentTypeLabel || file.statusLabel) && (
            <Flex gap={4} align="center" wrap="wrap">
              {file.documentTypeLabel && (
                <Text type="secondary" className="text-xs" ellipsis>
                  {file.documentTypeLabel}
                </Text>
              )}
              {file.statusLabel && (
                <Tag className="!m-0 !text-xs" bordered={false} color="default">
                  {file.statusLabel}
                </Tag>
              )}
            </Flex>
          )}
        </Flex>
      </Card>
    )
  }
)

FileCard.displayName = "FileCard"
